import { userAPI } from '@/utils/api'

const state = {
    // 用户基本信息
    profile: null,

    // 用户偏好设置
    preferences: {
        defaultSystem: null,
        autoRedirect: true,
        notifications: true,
        language: 'zh-CN'
    },

    // 用户活动记录
    activities: [],

    // 用户权限
    permissions: [],

    // 最近访问的系统
    recentSystems: []
}

const getters = {
    profile: state => state.profile,
    preferences: state => state.preferences,
    activities: state => state.activities,
    permissions: state => state.permissions,
    recentSystems: state => state.recentSystems,

    // 用户显示名称
    displayName: state => {
        if (!state.profile) return ''
        return state.profile.name ||
            `${state.profile.first_name || ''} ${state.profile.last_name || ''}`.trim() ||
            state.profile.username ||
            state.profile.email
    },

    // 用户头像
    avatar: state => {
        if (!state.profile) return ''
        return state.profile.avatar || '/assets/default-avatar.png'
    },

    // 用户角色
    roles: state => {
        if (!state.profile) return []
        return state.profile.roles || []
    },

    // 检查用户是否有特定权限
    hasPermission: (state) => (permission) => {
        return state.permissions.includes(permission)
    },

    // 检查用户是否有特定角色
    hasRole: (state) => (role) => {
        const roles = state.profile?.roles || []
        return roles.includes(role)
    },

    // 获取默认系统
    defaultSystem: state => {
        return state.preferences.defaultSystem
    }
}

const mutations = {
    // 设置用户资料
    SET_PROFILE(state, profile) {
        state.profile = profile
    },

    // 更新用户偏好
    UPDATE_PREFERENCES(state, preferences) {
        state.preferences = { ...state.preferences, ...preferences }
    },

    // 设置用户权限
    SET_PERMISSIONS(state, permissions) {
        state.permissions = permissions
    },

    // 添加活动记录
    ADD_ACTIVITY(state, activity) {
        state.activities.unshift({
            id: Date.now() + Math.random(),
            timestamp: new Date(),
            ...activity
        })

        // 限制活动记录数量
        if (state.activities.length > 100) {
            state.activities = state.activities.slice(0, 100)
        }
    },

    // 添加最近访问的系统
    ADD_RECENT_SYSTEM(state, system) {
        // 移除已存在的相同系统
        state.recentSystems = state.recentSystems.filter(s => s.id !== system.id)

        // 添加到开头
        state.recentSystems.unshift({
            ...system,
            lastAccessed: new Date()
        })

        // 限制数量
        if (state.recentSystems.length > 10) {
            state.recentSystems = state.recentSystems.slice(0, 10)
        }
    },

    // 设置默认系统
    SET_DEFAULT_SYSTEM(state, systemId) {
        state.preferences.defaultSystem = systemId
    },

    // 清除用户数据
    CLEAR_USER_DATA(state) {
        state.profile = null
        state.preferences = {
            defaultSystem: null,
            autoRedirect: true,
            notifications: true,
            language: 'zh-CN'
        }
        state.activities = []
        state.permissions = []
        state.recentSystems = []
    },

    // 更新用户资料字段
    UPDATE_PROFILE_FIELD(state, { field, value }) {
        if (state.profile) {
            state.profile[field] = value
        }
    },

    // 设置活动记录
    SET_ACTIVITIES(state, activities) {
        state.activities = activities
    },

    // 清除活动记录
    CLEAR_ACTIVITIES(state) {
        state.activities = []
    }
}

const actions = {
    // 获取用户资料
    async fetchProfile({ commit }) {
        try {
            const response = await userAPI.getProfile()

            if (response.success) {
                commit('SET_PROFILE', response.user)
                commit('SET_PERMISSIONS', response.permissions || [])

                // 加载用户偏好
                await this.dispatch('user/loadPreferences')

                console.log('✅ 用户资料获取成功')
                return response.user
            } else {
                throw new Error(response.message || '获取用户资料失败')
            }
        } catch (error) {
            console.error('❌ 获取用户资料失败:', error)
            throw error
        }
    },

    // 更新用户资料
    async updateProfile({ commit, state }, profileData) {
        try {
            const response = await userAPI.updateProfile(profileData)

            if (response.success) {
                commit('SET_PROFILE', { ...state.profile, ...profileData })

                // 记录活动
                commit('ADD_ACTIVITY', {
                    type: 'profile_update',
                    description: '更新个人资料',
                    data: Object.keys(profileData)
                })

                console.log('✅ 用户资料更新成功')
                return response.user
            } else {
                throw new Error(response.message || '更新用户资料失败')
            }
        } catch (error) {
            console.error('❌ 更新用户资料失败:', error)
            throw error
        }
    },

    // 更改密码
    async changePassword({ commit }, { currentPassword, newPassword }) {
        try {
            const response = await userAPI.changePassword({
                currentPassword,
                newPassword
            })

            if (response.success) {
                // 记录活动
                commit('ADD_ACTIVITY', {
                    type: 'password_change',
                    description: '修改密码'
                })

                console.log('✅ 密码修改成功')
                return true
            } else {
                throw new Error(response.message || '密码修改失败')
            }
        } catch (error) {
            console.error('❌ 密码修改失败:', error)
            throw error
        }
    },

    // 加载用户偏好
    async loadPreferences({ commit }) {
        try {
            const saved = localStorage.getItem('user_preferences')
            if (saved) {
                const preferences = JSON.parse(saved)
                commit('UPDATE_PREFERENCES', preferences)
            }

            // 从服务器同步偏好设置
            const response = await userAPI.getPreferences()
            if (response.success) {
                commit('UPDATE_PREFERENCES', response.preferences)
                this.dispatch('user/savePreferences')
            }
        } catch (error) {
            console.warn('⚠️ 加载用户偏好失败:', error)
        }
    },

    // 保存用户偏好
    async savePreferences({ state }) {
        try {
            // 保存到本地存储
            localStorage.setItem('user_preferences', JSON.stringify(state.preferences))

            // 同步到服务器
            await userAPI.updatePreferences(state.preferences)

            console.log('✅ 用户偏好保存成功')
        } catch (error) {
            console.warn('⚠️ 保存用户偏好失败:', error)
        }
    },

    // 更新用户偏好
    async updatePreferences({ commit, dispatch }, preferences) {
        commit('UPDATE_PREFERENCES', preferences)
        await dispatch('savePreferences')
    },

    // 记录系统访问
    recordSystemAccess({ commit }, system) {
        commit('ADD_RECENT_SYSTEM', system)
        commit('ADD_ACTIVITY', {
            type: 'system_access',
            description: `访问 ${system.name}`,
            data: { systemId: system.id, systemName: system.name }
        })

        // 保存到本地存储
        try {
            const recentSystems = this.state.user.recentSystems
            localStorage.setItem('recent_systems', JSON.stringify(recentSystems))
        } catch (error) {
            console.warn('保存最近访问系统失败:', error)
        }
    },

    // 设置默认系统
    async setDefaultSystem({ commit, dispatch }, systemId) {
        commit('SET_DEFAULT_SYSTEM', systemId)
        await dispatch('updatePreferences', { defaultSystem: systemId })

        commit('ADD_ACTIVITY', {
            type: 'default_system_change',
            description: '更改默认系统',
            data: { systemId }
        })
    },

    // 获取用户活动记录
    async fetchActivities({ commit }) {
        try {
            const response = await userAPI.getActivities()

            if (response.success) {
                // 将服务器的活动记录与本地记录合并
                const serverActivities = response.activities || []
                const localActivities = this.state.user.activities

                // 合并并去重
                const allActivities = [...serverActivities, ...localActivities]
                const uniqueActivities = allActivities.filter((activity, index, arr) =>
                    arr.findIndex(a => a.id === activity.id) === index
                )

                // 按时间排序
                uniqueActivities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

                commit('SET_ACTIVITIES', uniqueActivities.slice(0, 100))

                console.log('✅ 用户活动记录获取成功')
            }
        } catch (error) {
            console.warn('⚠️ 获取用户活动记录失败:', error)
        }
    },

    // 清除用户活动记录
    clearActivities({ commit }) {
        commit('CLEAR_ACTIVITIES')
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
} 